import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import Modal from '~ComponentsImproved/Modal';
import {
  combineKdsOrderItems,
  OrderItemWrapper,
} from '~Scenes/EventedKitchenDisplay/Components/Order';
import { GetPendingOrders_venue_orders } from '~Queries/__queries__';
import { Item, ORDER_STATUS, SERVICE_TYPE } from '@baemingo/order-status-events/dist/types';
import OrderItem from '~Scenes/EventedKitchenDisplay/Components/OrderItem';
import { Box, GreenButton, RedButton } from '~Styles';
import useI18n from '~Translations';
import { TranslatedString } from '~Types';
import PredefinedMessages from '~Components/PredefinedMessages';
import { DateTime } from 'luxon';
import { formatOrderType } from '~utilities';
import OrderETA from '~Scenes/EventedKitchenDisplay/Components/Order/OrderETA';
import { useIncomingAppOrders } from '~Providers/IncomingAppOrdersProvider';
import { useButton, useDispatch, useLogger } from '~Hooks';
import * as actions from '~/actions';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useStorage } from '~Components/Storage';

const Wrapper = styled.div`
  border-radius: 4px;
  background-color: #fff;
  margin-bottom: 32px;
`;

const OrderNumberWrapper = styled.div`
  background-color: #ffc000;
  padding: 4px 16px;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  min-height: 40px;
  border-radius: 4px 4px 0 0;
`;

const OrderId = styled.span`
  font-size: 20px;
  text-align: center;
  letter-spacing: 0.24px;
  text-transform: uppercase;
  margin-right: auto;
`;

const DesiredTimeBar = styled.div`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.4px;
  line-height: 16px;
  text-transform: uppercase;
  color: #fff;
  background-color: #1c232c;
  border-radius: 16px;
  margin-left: auto;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const Comment = styled.div`
  display: flex;
  color: #333;
  background: #fff;
  padding: 10px 5px 10px 10px;
  font-size: 14px;
  font-style: italic;
  border-bottom: 1px solid #d5d7d8;
`;

const ItemBox = styled.div`
  border-bottom: 1px solid #d5d7d8;
`;

const DesiredTime = styled.span`
  color: #fff;
  font-size: 20px;
`;

const rejectReasons = (i18n: (obj: string | TranslatedString | null) => string) => [
  {
    id: 'TOO_MANY_ORDERS',
    label: i18n('ReasonsDecline.ToMuchToDoRightNow'),
  },
  {
    id: 'OUT_OF_SUPPLIES',
    label: i18n('ReasonsDecline.EndOfGoods'),
  },
  {
    id: 'DELIVERY_IS_NOT_AVAILABLE',
    label: i18n('ReasonsDecline.DeliverNotAvailable'),
  },
  {
    id: 'CLOSING_SOON',
    label: i18n('ReasonsDecline.WeWillCloseSoon'),
  },
];

interface Props {
  order: GetPendingOrders_venue_orders;
}

const IncomingOrder: FC<Props> = ({ order }) => {
  const logger = useLogger('incoming-app-orders');
  const [isLoading, setIsLoading] = useState(false);
  const [showingRejectReasons, setShowingRejectReasons] = useState(false);
  const { i18n } = useI18n();
  const incomingAppOrders = useIncomingAppOrders();
  const [storage] = useStorage();
  const [pickupTimeRaw, setPickupTimeRaw] = useState<string | null>(null);
  const dispatch = useDispatch();

  const handleAccept = async (orderToAccept: GetPendingOrders_venue_orders) => {
    setIsLoading(true);
    logger.info(`Manually accepting online order ${orderToAccept.easyId}`, {
      orderData: {
        orderId: orderToAccept.id,
        pickuptime: pickupTimeRaw,
        orderDesiredTime: orderToAccept.desiredTime,
      },
    });
    try {
      await incomingAppOrders.acceptOrder({
        ...orderToAccept,
        estimatedFinishTime: pickupTimeRaw || orderToAccept.estimatedFinishTime,
      });

      setIsLoading(false);
    } catch (error) {
      logger.warn(
        `Error manually accepting online order ${orderToAccept.easyId}, error shown to user`,
        {
          orderId: orderToAccept.id,
        },
      );

      dispatch(
        actions.addToast(i18n('KDS.IncomingOrders.FailedAccept'), true, 'order-accept', true),
      );
      setIsLoading(false);
    }
  };

  const handleRejectOrder = async (orderId: string, reason: string) => {
    setIsLoading(true);
    logger.info(`Manually rejecting online order`, {
      orderId,
      reason,
    });
    try {
      await incomingAppOrders.rejectOrder(orderId, reason);
      setIsLoading(false);
    } catch (error) {
      logger.warn(`Error manually rejecting online order, error shown to user`, {
        orderId,
        reason,
      });

      dispatch(
        actions.addToast(i18n('KDS.IncomingOrders.FailedAccept'), true, 'order-accept', true),
      );
      setIsLoading(false);
    }
  };

  const formattedItems: Item[] = order.items.map((item) => ({
    id: item.id,
    itemId: item.entry.item.id,
    categoryId: item.entry.item.categories[0].id,
    orderId: order.id,
    status: ORDER_STATUS.PENDING,
    courseId: null,
    comment: item.message,
    variations: item.variations.map((variation) => variation.id),
    removedVariations: item.removedVariations.map((variation) => variation.id),
    parentId: null,
  }));

  const combinedItems = combineKdsOrderItems(formattedItems, false);

  const acceptButton = useButton({
    onClick: () => !isLoading && handleAccept(order),
    disabled: isLoading,
  });

  const rejectButton = useButton({
    onClick: () => !isLoading && setShowingRejectReasons(true),
    disabled: isLoading,
  });

  const pickupTime = order.desiredTime ? DateTime.fromISO(order.desiredTime) : null;

  //If pickup time is today show time only
  const formattedPickupTime = pickupTime
    ? pickupTime.toFormat(pickupTime.hasSame(DateTime.now(), 'day') ? 'HH:mm' : 'yyyy-MM-dd HH:mm')
    : null;

  return (
    <>
      <Wrapper>
        <OrderNumberWrapper>
          <OrderId>
            {`#${order.easyId || order.id.slice(0, 4)} ${formatOrderType(
              order.type || SERVICE_TYPE.EAT_HERE,
              i18n,
            )}`}
          </OrderId>
          {order.desiredTime && formattedPickupTime && (
            <DesiredTimeBar>
              <DesiredTime>
                {i18n('KDS.IncomingOrders.Pickup')} {formattedPickupTime}
              </DesiredTime>
            </DesiredTimeBar>
          )}
        </OrderNumberWrapper>

        {combinedItems.map((item) => {
          return (
            <ItemBox key={item.id}>
              <OrderItemWrapper padding={false} isGroup={false} itemStatusColor="#515A5A">
                <OrderItem
                  isBuffering={false}
                  item={item}
                  count={item.count}
                  isChild={false}
                  disableItemProgression={true}
                />
              </OrderItemWrapper>
            </ItemBox>
          );
        })}

        {order.comment && <Comment>{order.comment}</Comment>}
        <OrderETA
          orderId={order.id}
          estimatedFinishTime={
            // pickupTimeRaw is the manually adjusted time with the +/- buttons
            pickupTimeRaw ||
            DateTime.now()
              .plus({ minutes: incomingAppOrders.prepTime.time })
              .toISO()
          }
          onChangePickupTime={(newTime) => setPickupTimeRaw(newTime)}
          disabled={isLoading}
        />

        <Box gap="medium" display="flex" padding="small">
          <GreenButton {...acceptButton} style={{ width: '100%' }} disabled={isLoading}>
            {i18n('KDS.Accept')}
          </GreenButton>
          <RedButton {...rejectButton} style={{ width: '100%' }} disabled={isLoading}>
            {i18n('KDS.Reject')}
          </RedButton>
        </Box>
      </Wrapper>
      <Modal
        isShowing={showingRejectReasons}
        hide={() => setShowingRejectReasons(false)}
        position="flex-start">
        <div>
          <Box display="flex" justifyContent="center">
            <Heading level={1} color="#fff" bottom={32}>
              {i18n('ReasonsDecline.WhyDenyOrder')}
            </Heading>
          </Box>
          <PredefinedMessages
            hideTextarea={true}
            onSubmit={(reason) => {
              if (!showingRejectReasons) return;

              handleRejectOrder(order.id, typeof reason === 'string' ? reason : reason.id);
              setShowingRejectReasons(false);
            }}
            messages={rejectReasons(i18n)}
            width={500}></PredefinedMessages>
        </div>
      </Modal>
    </>
  );
};

export default IncomingOrder;
